// MIDAS V3 — <TelRefV3>
//
// Render layer for the bot's telemetry provenance tag (MASH-CITE-CHIPS):
//   Windowed: <tel-ref signal_key="part" window="this_month" count="10386">part (this_month, n=10386)</tel-ref>
//   Snapshot: <tel-ref signal_key="run_state" window="current">run state (latest snapshot)</tel-ref>
//
// Spec: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/TEL_REF_RENDERER_SPEC.md
//
// This is a near-clone of <LiveRefV3> (LiveRefV3.tsx) adapted from the PLC
// live-value contract (address/value) to the telemetry provenance contract
// (signal_key / window / count). The two chips are the same FAMILY but
// visually distinguishable: <tel-ref> uses a STATIC pip + a muted "TEL"
// mini-label (no pulsing animation) because telemetry provenance cites a
// stored aggregate, not a live-polled value.
//
// Windowed vs snapshot: driven by `count` present (number) vs absent
// (undefined) — NOT by parsing `window`. The parser (markdownComponentsV3
// decodeTelRefBody) has already resolved this to `count?: number`; an
// undefined count renders the localized "latest snapshot" string in the
// tooltip Samples slot.
//
// Tooltip parity notes (mirrors LiveRefV3 exactly):
//   • Radix Tooltip from ../v2/ui/tooltip. TooltipProvider is mounted ONCE at
//     MidasV3Layout.tsx — we do NOT mount our own here.
//   • TooltipContent sets an explicit opaque surface via V3 tokens
//     (background: var(--card) etc.) because the shadcn `bg-popover` token is
//     undefined on .midas-v3-root and renders transparent otherwise. See the
//     long comment in LiveRefV3.tsx:168-196.
//   • onClick defaults to a console.debug placeholder — there is no
//     artifact-pane deep-link for telemetry yet; the prop is kept so a
//     follow-up can wire it without touching the parser.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens — match V3's MUI-free invariant.
// Plain inline styles + scoped CSS classes from ./tel-ref.css.
import * as React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../v2/ui/tooltip";

export interface TelRefV3Props {
  /** Store-mapped metric key — e.g. "part", "reject", "run_state". Opaque to the renderer. */
  signalKey: string;
  /** Resolved aggregation window — e.g. "this_month", "today", or "current" (snapshot). */
  window: string;
  /** Sample size on the windowed form; undefined on the snapshot form. */
  count?: number;
  /** Verbatim inner-text chip face — e.g. "part (this_month, n=10386)". */
  label: string;
  /**
   * Open the telemetry watcher / artifact pane. Hard-deferred (no deep-link
   * for telemetry yet). Default is a console.debug placeholder so the click is
   * observable in DevTools during demos. Kept so a follow-up can swap in a real
   * handler without touching the parser.
   */
  onClick?: (signalKey: string) => void;
}

const defaultOnClick = (signalKey: string): void => {
  // Placeholder — real artifact-pane wiring is a follow-up. We keep an
  // observable trace so manual demo / QA can confirm the chip is interactive.
  // eslint-disable-next-line no-console
  console.debug("[TelRefV3] click (no-op placeholder)", { signalKey });
};

export const TelRefV3: React.FC<TelRefV3Props> = ({
  signalKey,
  window: windowAttr,
  count,
  label,
  onClick,
}) => {
  const { translate, i18n } = useTranslation("chatBot");

  const handleClick = React.useCallback(() => {
    if (onClick) {
      onClick(signalKey);
    } else {
      defaultOnClick(signalKey);
    }
  }, [signalKey, onClick]);

  const signalLabel = translate("telRef.tooltip.signal", {
    defaultValue: "Signal:",
  }) as string;
  const windowLabel = translate("telRef.tooltip.window", {
    defaultValue: "Window:",
  }) as string;
  const samplesLabel = translate("telRef.tooltip.samples", {
    defaultValue: "Samples:",
  }) as string;
  const snapshotLabel = translate("telRef.tooltip.snapshot", {
    defaultValue: "latest snapshot",
  }) as string;
  const ariaChip = translate("telRef.aria.chip", {
    defaultValue: "Telemetry provenance {{signalKey}} ({{window}})",
    signalKey,
    window: windowAttr,
  }) as string;

  // Thousands separators in the active language (EN 10,386 / SK 10 386).
  // Only the tooltip Samples value is re-formatted; the chip face shows the
  // bot's inner-text label verbatim. Intl.NumberFormat handles locale grouping;
  // we never hand-roll separator logic.
  const samplesValue =
    count !== undefined
      ? new Intl.NumberFormat(i18n.language || undefined).format(count)
      : snapshotLabel;

  const chip = (
    <span
      className="tel-ref"
      role="button"
      tabIndex={0}
      aria-label={ariaChip}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <span className="tel-ref__pip" aria-hidden />
      <span className="tel-ref__label">TEL</span>
      <span className="tel-ref__face">{label}</span>
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent
        side="top"
        style={{
          // Explicit opaque surface via V3 tokens — the shadcn `bg-popover`
          // token is undefined on .midas-v3-root and renders transparent.
          // See the long rationale in LiveRefV3.tsx:168-196.
          background: "var(--card)",
          color: "var(--ink)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow)",
          borderRadius: 6,
          padding: "8px 10px",
          fontFamily: "var(--font-sans)",
          fontSize: 12,
          lineHeight: 1.4,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div>
            <span style={{ color: "var(--muted)", marginRight: 6 }}>
              {signalLabel}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", color: "var(--ink)" }}>
              {signalKey}
            </span>
          </div>
          <div>
            <span style={{ color: "var(--muted)", marginRight: 6 }}>
              {windowLabel}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", color: "var(--ink)" }}>
              {windowAttr}
            </span>
          </div>
          <div>
            <span style={{ color: "var(--muted)", marginRight: 6 }}>
              {samplesLabel}
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontWeight: 600,
                color: "var(--ink)",
              }}
            >
              {samplesValue}
            </span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default TelRefV3;
